/**
 * WHERE A SESSION OF THIS AGENT'S OPENS — asked of the agent, not decided by
 * the platform.
 *
 * A session is a conversation, or a protocol run: a session that pins no
 * agent, started from the protocols agent's /runs/new screen, which
 * sessionSurfaceSlug hands to the protocols agent; that agent's app is its own
 * service (tricorder-studio-web), which decides at /sessions/<id> where a
 * session opens (RemoteAgentFace). An agent that needs a session
 * opened off its face states it in `apps/<slug>/src/sessionEntry.ts`, and the
 * platform asks.
 *
 * DISCOVERED, NOT REGISTERED — the same convention as the face and the route
 * tree beside it. An agent whose sessions are all conversations ships no
 * module and needs no entry here.
 */
import { sessionSurfaceSlug } from '@/lib/sessionRouting'
import type { SessionResponse } from '@/api/types_session'
import { agentSlugForDir, slugFromAppModulePath } from './appModulePath'

/** The identifying fields an entry decision may read off a session. */
export type AgentSessionRef = Pick<SessionResponse, 'id' | 'agent'>

export interface AgentSessionEntryModule {
  /**
   * The agent's own path for this session, or null when it opens on the face.
   * Same path on both surfaces — the frame prefixes it with the agent's base,
   * the sub-app serves it at its root.
   */
  entryPathForSession: (session: AgentSessionRef) => string | null
}

// Vite requires a LITERAL glob — the pattern IS the convention.
const DISCOVERED_ENTRIES = import.meta.glob<AgentSessionEntryModule>(
  '../../../../apps/*/src/sessionEntry.ts',
  { eager: true },
)

// Keyed on the agent's DECLARED slug, not the directory — see
// appModulePath.ts's agentSlugForDir.
export const AGENT_SESSION_ENTRIES: Record<string, AgentSessionEntryModule> = Object.fromEntries(
  Object.entries(DISCOVERED_ENTRIES).map(([path, mod]) => [
    agentSlugForDir(slugFromAppModulePath(path, 'sessionEntry.ts')),
    mod,
  ]),
)

/**
 * The agent's own path for this session, or `/` for its face.
 *
 * Never returns null — "no opinion" and "the face" are the same instruction,
 * and collapsing them here keeps each call site from inventing a default.
 */
export function agentEntryPathForSession(session: AgentSessionRef): string {
  return AGENT_SESSION_ENTRIES[sessionSurfaceSlug(session)]?.entryPathForSession(session) ?? '/'
}

/**
 * True when this session opens on one of its agent's own screens rather than
 * on its face — i.e. it is not a conversation to read.
 *
 * Home uses it to keep such a session from becoming the pane's default (there
 * is nothing to read there), and `navigateToSession` to send a full-screen
 * open to the right screen of the sub-app.
 */
export function opensOffAgentFace(session: AgentSessionRef): boolean {
  return agentEntryPathForSession(session) !== '/'
}
